import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { useAtom } from 'jotai';
import { MAlert } from '../components/Monetize';
import { recordShareState } from '../components/RecordShare/record-share.utils';
import { ExternalShareErrorPage } from './components/ExternalShareErrorPage';

// Example URL:
// /share/tennt_VW00CDXt5Q26Lfor/quotes/quot_CSZ5LNW7IAA9j?token=2b76d2cc-e45d-423d-8e5e-23bef4e8f970

interface AnonymousRecordShareWithCodeVerifierLayoutProps {
  recordIdPathParamName?: string;
  children: ReactNode;
}

/**
 * Layout which requires login using a secret+code verifier
 * This layout managed the login process and stores the code verifier in local storage
 *
 * Jotai is used to manage setting/accessing the authentication state
 * and child components should only rely on the atom without having to manage any URL parameters
 */
export const AnonymousRecordShareWithCodeVerifierLayout = ({
  recordIdPathParamName: recordIdPathParmName = 'recordId',
  children,
}: AnonymousRecordShareWithCodeVerifierLayoutProps) => {
  const pathParams = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [errorMessage, setErrorMessage] = useState(() =>
    searchParams.get('error'),
  );

  const storeParams = useMemo(
    () => ({
      tenantId: pathParams.tenantId,
      recordId: pathParams[recordIdPathParmName],
      token: searchParams.get('token'),
    }),
    [pathParams, recordIdPathParmName, searchParams],
  );

  const { tenantId, recordId, token } = storeParams;

  const [shareState, setShareState] = useAtom(recordShareState);

  useEffect(() => {
    if (searchParams.has('error')) {
      setErrorMessage(searchParams.get('error'));
      searchParams.delete('error');
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    setShareState({ tenantId, recordId, token });
  }, [tenantId, recordId, token, setShareState]);

  const hasAccessError = !tenantId || !recordId || !token;

  // Show error if we are missing key elements from URL
  if (hasAccessError) {
    return (
      <ExternalShareErrorPage
        title="You don't have permission to view this quote."
        description="Contact the sender to request access or have them re-share the quote with you."
      />
    );
  }

  if (!shareState) {
    return null;
  }

  if (errorMessage) {
    return (
      <ExternalShareErrorPage title="Access Error">
        <MAlert type="error">{errorMessage}</MAlert>
      </ExternalShareErrorPage>
    );
  }

  // TODO: login screen once we enable this feature
  // Show the login screen if the code verifier is not set
  // if (!codeVerifier) {
  //   return (
  //     <RecordShareLogin
  //       tenantId={tenantId}
  //       recordId={recordId}
  //       token={token}
  //       email={email}
  //       errorMessage={errorMessage}
  //       onVerified={handleSetCodeVerifier}
  //     />
  //   );
  // }

  // Allow user to continue if code verifier is set
  // Child components should interact with store to clear the codeVerifier if it is invalid
  return children;
};
